import normalize from "json-api-normalizer";
import { get, isEmpty } from "lodash";
import { push } from "connected-react-router";

import * as actionTypes from "./types";
import BundlesService from "../services/bundles.service";
import NotificationService from "../services/notification.service";
import EncodingFiltersService from "../services/encoding-filters.service";

export const initBundles = params => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: actionTypes.ASYNC_START });

  try {
    const bundleResult = await BundlesService.getBundles(tenantSlug, EncodingFiltersService.encode(params));
    const bundles = normalize(bundleResult.data);

    dispatch(setBundles(bundles));
    dispatch({ type: actionTypes.ASYNC_END });
  } catch (e) {
    dispatch(setBundlesFailed());
    dispatch({ type: actionTypes.ASYNC_END });
    NotificationService.addError(`Failed fetching the bundles! ${NotificationService.handleMessage(e)}`);
  }
};

export const fetchUnfilteredBundles = () => initBundles({ archived: "true,false" });

export const fetchBundle = slug => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    const bundleResult = await BundlesService.get(slug, tenantSlug);
    const normalizedBundles = normalize(bundleResult.data);

    dispatch(setBundles(normalizedBundles));
  } catch (e) {
    dispatch(setBundlesFailed());
    NotificationService.addError(`Failed fetching the bundle! ${NotificationService.handleMessage(e)}`);
  }
};

export const toggleArchivedBundle = (slug, data) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");
  dispatch({ type: actionTypes.ASYNC_START });

  try {
    await BundlesService.put(slug, tenantSlug, data);
    dispatch(bundleSaved());
    dispatch({ type: actionTypes.ASYNC_END });
    NotificationService.addSuccess(`Bundle successfully ${data.archived ? "archived!" : "activated!"}`);
    // this is currently needed because the UI doesn't retain the data
    dispatch(initBundles({ archived: !data.archived }));
  } catch (e) {
    dispatch(bundleSaveFailed());
    dispatch({ type: actionTypes.ASYNC_END });
    NotificationService.addError(
      `Failed ${data.archived ? "archiving!" : "activating!"} the bundle! ${NotificationService.handleMessage(e)}`
    );
  }
};

export const createBundle = (bundleAttributes, bundlePricesAttributes) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    const createBundleResponse = await BundlesService.create(bundleAttributes, tenantSlug);

    if (createBundleResponse.status !== 201) {
      return dispatch(bundleSaveFailed());
    }

    const bundleSlug = get(createBundleResponse, "data.data.id");

    if (!isEmpty(bundlePricesAttributes)) {
      await Promise.all(
        bundlePricesAttributes.prices
          .filter(price => !price.removed)
          .map(price => BundlesService.createPrice(bundleSlug, tenantSlug, price))
      );
    }

    dispatch(bundleSaved());
    dispatch(push("/bundles"));
    NotificationService.addSuccess("Bundle successfully created!");
  } catch (e) {
    dispatch(bundleSaveFailed());
    NotificationService.addError(`Failed creating the bundle! ${NotificationService.handleMessage(e)}`);
  }
};

export const editBundle = (slug, bundleAttributes, bundlePricesAttributes) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    const updateBundleResponse = await BundlesService.put(slug, tenantSlug, bundleAttributes);

    if (!isEmpty(bundlePricesAttributes)) {
      await Promise.all(
        bundlePricesAttributes.bundlePrices.map(price => {
          if (price.id && price.removed) {
            BundlesService.deletePrice(price.id, tenantSlug);
          } else if (price.id && !price.removed) {
            BundlesService.updatePrice(price.id, tenantSlug, price);
          } else if (!price.id && !price.removed) {
            BundlesService.createPrice(slug, tenantSlug, price);
          }
          return Promise.resolve(true);
        })
      );
    }

    dispatch(bundleSaved());
    dispatch(push("/bundles"));
    NotificationService.addSuccess("Bundle successfully updated!");
  } catch (e) {
    dispatch(bundleSaveFailed());
    NotificationService.addError(`Failed updating the bundle! ${NotificationService.handleMessage(e)}`);
  }
};

export const setBundles = bundles => {
  return {
    type: actionTypes.SET_BUNDLES,
    bundles: isEmpty(bundles) ? { bundles: {} } : bundles
  };
};

export const setBundlesFailed = () => {
  return {
    type: actionTypes.SET_BUNDLES_FAILED
  };
};

export const bundleSaved = () => {
  return {
    type: actionTypes.BUNDLE_SAVED
  };
};

export const bundleSaveFailed = () => {
  return {
    type: actionTypes.BUNDLE_SAVE_FAILED
  };
};
